/*
 * RiderSignup — /ride/new public onboarding flow (production-grade).
 * Step 1: Info + TOS/Privacy acceptance → POST /rider/register → SMS OTP
 * Step 2: OTP verify → POST /rider/verify-otp → token
 * Step 3: SetupIntent + Stripe Elements (with Apple/Google Pay) → card saved
 * Step 4: Redirect to /ride/{token}
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { autoPrefixUS } from "../components/PhoneInput";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import PageBackLink from "../components/PageBackLink";

let _stripePromise = null;
async function getStripe() {
  if (_stripePromise) return _stripePromise;
  const { data } = await apiClient.get("/rider/payments/config");
  _stripePromise = loadStripe(data.publishable_key);
  return _stripePromise;
}

export default function RiderSignup() {
  const navigate = useNavigate();
  const [step, setStep] = useState("info"); // info → otp → card → done
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", tos: false, privacy: false, smsConsent: false });
  const [otp, setOtp] = useState("");
  const [phoneNormalized, setPhoneNormalized] = useState("");
  const [channel, setChannel] = useState("voice"); // voice (default) → email fallback
  const [channelUsed, setChannelUsed] = useState(null); // returned by backend
  const [token, setToken] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => { getStripe().then(setStripePromise); }, []);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const submitInfo = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and mobile phone are required."); return; }
    if (!form.tos || !form.privacy) { toast.error("Please accept the Terms of Service and Privacy Policy to continue."); return; }
    if (!form.smsConsent) { toast.error("Please check the SMS/voice consent box to continue."); return; }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/rider/register", {
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() || null,
        tos_accepted: form.tos, privacy_accepted: form.privacy, sms_consent: form.smsConsent, channel,
      });
      setPhoneNormalized(data.phone);
      setChannelUsed(data.channel_used);
      setStep("otp");
      setResendCooldown(30);
      if (data.channel_used === "voice") toast.success("We're calling your phone with a 6-digit code.");
      else if (data.channel_used === "email_fallback") toast.info("Voice call failed — we emailed your code instead.");
      else toast.success(`Code sent to ${form.email.trim()}. Check your inbox.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Sign-up failed");
    } finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error("Enter the 6-digit code."); return; }
    setBusy(true);
    try {
      const { data: v } = await apiClient.post("/rider/verify-otp", { phone: phoneNormalized, code: otp });
      setToken(v.token);
      const { data: si } = await apiClient.post(`/rider/payments/setup-intent/${v.token}`);
      setClientSecret(si.client_secret);
      setStep("card");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  const resendOtp = async (newChannel) => {
    if (resendCooldown > 0) return;
    const useChannel = newChannel || channel;
    try {
      const { data } = await apiClient.post(
        `/rider/resend-otp?channel=${useChannel}`,
        { phone: phoneNormalized, code: "" }
      );
      setChannel(useChannel);
      setChannelUsed(data.channel_used);
      setResendCooldown(30);
      if (data.channel_used === "voice") toast.success("Calling you now with a new code.");
      else if (data.channel_used === "sms") toast.success("New code texted to your phone.");
      else toast.success(`New code emailed to ${form.email}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not resend");
    }
  };

  const onCardSaved = () => {
    toast.success("Card saved. You're ready to ride.");
    setStep("done");
    setTimeout(() => navigate(`/ride/${token}`), 800);
  };

  return (
    <div className="min-h-screen bg-[#F5F3EA]" data-testid="rider-signup-page">
      <PageBackLink fallbackTo="/" label="Back to home" />
      <header className="px-4 pt-6 pb-3">
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate-500 font-bold">MedexOVA Ride</div>
        <div className="text-sm text-slate-600">Sign up · Verify phone · Save a card</div>
      </header>

      <main className="px-4 pb-24 space-y-4 max-w-md mx-auto">
        {step === "info" && (
          <form onSubmit={submitInfo} className="rounded-2xl bg-white border border-[#E2E0D8] p-4 space-y-3 shadow-sm">
            <h2 className="font-heading text-xl font-bold text-slate_navy">Create your ride account</h2>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Full name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="rider-signup-name" className="w-full border border-[#E2E0D8] rounded-md py-2 px-3 mt-1" placeholder="Jane Doe" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Mobile phone (US)</label>
              <input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                onBlur={() => setForm((f) => ({ ...f, phone: autoPrefixUS(f.phone) }))}
                data-testid="rider-signup-phone" className="w-full border border-[#E2E0D8] rounded-md py-2 px-3 mt-1" placeholder="+1 555 000 0000" />
              <div className="text-[10px] text-slate-500 mt-1">We&apos;ll <b>call you</b> and read a 6-digit code to verify your number (no SMS needed).</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Email (for receipts)</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="rider-signup-email" className="w-full border border-[#E2E0D8] rounded-md py-2 px-3 mt-1" placeholder="you@email.com" />
              <div className="text-[10px] text-slate-500 mt-1">Email is used for receipts and as a backup if the phone call doesn&apos;t connect.</div>
            </div>
            <div className="pt-2 space-y-2 text-xs text-slate-600">
              <label className="flex items-start gap-2">
                <input type="checkbox" checked={form.tos} onChange={(e) => setForm({ ...form, tos: e.target.checked })} data-testid="rider-signup-tos" className="mt-0.5" />
                <span>I agree to the <Link to="/terms" className="text-forest font-semibold underline">Terms of Service</Link>.</span>
              </label>
              <label className="flex items-start gap-2">
                <input type="checkbox" checked={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.checked })} data-testid="rider-signup-privacy" className="mt-0.5" />
                <span>I&apos;ve read the <Link to="/privacy" className="text-forest font-semibold underline">Privacy Policy</Link>.</span>
              </label>
              <label className="flex items-start gap-2 bg-[#F5F3EA] p-2 rounded-md" data-testid="rider-signup-sms-consent-wrap">
                <input type="checkbox" checked={form.smsConsent} onChange={(e) => setForm({ ...form, smsConsent: e.target.checked })}
                  data-testid="rider-signup-sms-consent" className="mt-0.5" />
                <span>
                  I agree to receive automated SMS and voice messages from <b>MedexOVA</b> about my ride
                  — trip confirmations, driver arrival, dispatcher updates, and account verification codes.
                  Message frequency varies by trip activity. Msg &amp; data rates may apply. Reply{" "}
                  <b>STOP</b> to cancel, <b>HELP</b> for help. See our{" "}
                  <Link to="/sms-terms" className="text-forest font-semibold underline">SMS Terms</Link>.
                </span>
              </label>
            </div>
            <div className="text-[11px] text-slate-500 leading-snug bg-[#F5F3EA] p-2 rounded-md">
              Next: verify your phone, then save a payment card. When you request a ride, we place a temporary <b>authorization hold</b> for the quoted fare.
              You&apos;re only charged if the trip is completed. Canceled or unfulfilled requests release the hold automatically.
            </div>
            <button type="submit" disabled={busy} data-testid="rider-signup-continue"
              className="w-full py-3 rounded-2xl bg-forest hover:bg-forest-hover text-white font-bold shadow disabled:opacity-60">
              {busy ? "Sending code…" : "Continue"}
            </button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={submitOtp} className="rounded-2xl bg-white border border-[#E2E0D8] p-4 space-y-3 shadow-sm">
            <h2 className="font-heading text-xl font-bold text-slate_navy">Enter your 6-digit code</h2>
            <div className="text-xs text-slate-500">
              {channelUsed === "voice" && <>We&apos;re calling <b>{phoneNormalized}</b> — an automated voice will read your code twice.</>}
              {channelUsed === "email" && <>We emailed the code to <b>{form.email}</b>. Check your inbox (and spam).</>}
              {channelUsed === "sms" && <>We texted the code to <b>{phoneNormalized}</b>.</>}
              {channelUsed === "email_fallback" && <>Voice call couldn&apos;t reach you — we emailed the code to <b>{form.email}</b> instead.</>}
              {!channelUsed && <>Code sent. It expires in 10 minutes.</>}
            </div>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
              data-testid="rider-signup-otp"
              className="w-full border-2 border-[#E2E0D8] rounded-md py-3 px-3 text-center text-2xl font-mono tracking-[0.4em] focus:border-forest outline-none"
              placeholder="——————"
              autoFocus
            />
            <button type="submit" disabled={busy || otp.length !== 6} data-testid="rider-signup-verify"
              className="w-full py-3 rounded-2xl bg-forest hover:bg-forest-hover text-white font-bold shadow disabled:opacity-60">
              {busy ? "Verifying…" : "Verify & continue"}
            </button>
            <div className="flex flex-col gap-1 text-center">
              <button type="button" onClick={() => resendOtp()} disabled={resendCooldown > 0} data-testid="rider-signup-resend"
                className="text-xs text-forest font-semibold disabled:text-slate-400">
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : (channel === "voice" ? "Didn't get the call? Call me again" : "Didn't get it? Resend")}
              </button>
              {channel === "voice" && (
                <button type="button" onClick={() => resendOtp("sms")} data-testid="rider-signup-switch-sms"
                  className="text-[11px] text-slate-500 underline">
                  Or text me the code instead
                </button>
              )}
              {form.email && channel !== "email" && (
                <button type="button" onClick={() => resendOtp("email")} data-testid="rider-signup-switch-email"
                  className="text-[11px] text-slate-500 underline">
                  Or email me the code instead
                </button>
              )}
              {channel !== "voice" && (
                <button type="button" onClick={() => resendOtp("voice")} data-testid="rider-signup-switch-voice"
                  className="text-[11px] text-slate-500 underline">
                  Or call me with the code instead
                </button>
              )}
            </div>
            <button type="button" onClick={() => setStep("info")} className="w-full text-[11px] text-slate-500 underline">
              ← Change phone number
            </button>
          </form>
        )}

        {step === "card" && clientSecret && stripePromise && (
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
            <CardCollector token={token} onSaved={onCardSaved} />
          </Elements>
        )}

        {step === "done" && (
          <div className="rounded-2xl bg-white border border-forest p-6 text-center space-y-2">
            <div className="text-3xl">🎉</div>
            <div className="font-heading text-lg font-bold text-slate_navy">You&apos;re all set</div>
            <div className="text-xs text-slate-500">Taking you to your ride page…</div>
          </div>
        )}
      </main>
    </div>
  );
}

function CardCollector({ token, onSaved }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErr(null);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: `${window.location.origin}/ride/${token}` },
      redirect: "if_required",
    });
    if (error) {
      setErr(error.message || "Card could not be saved");
      setBusy(false);
      return;
    }
    if (setupIntent?.payment_method) {
      try {
        await apiClient.post("/rider/payments/methods/set-default", { token, payment_method_id: setupIntent.payment_method });
      } catch { /* non-fatal */ }
    }
    setBusy(false);
    onSaved();
  };
  return (
    <form onSubmit={submit} className="rounded-2xl bg-white border border-[#E2E0D8] p-4 space-y-3 shadow-sm">
      <h2 className="font-heading text-xl font-bold text-slate_navy">Save a payment method</h2>
      <div className="text-[11px] text-slate-500 leading-snug">
        Card is stored securely with Stripe. Nothing posts to your card until a driver completes your trip.
      </div>
      <div data-testid="rider-signup-card-el">
        <PaymentElement options={{ wallets: { applePay: "auto", googlePay: "auto" } }} />
      </div>
      {err && <div className="text-xs text-red-600" data-testid="rider-signup-card-error">{err}</div>}
      <button type="submit" disabled={!stripe || busy} data-testid="rider-signup-save-card"
        className="w-full py-3 rounded-2xl bg-forest hover:bg-forest-hover text-white font-bold shadow disabled:opacity-60">
        {busy ? "Saving…" : "Save card"}
      </button>
    </form>
  );
}
